import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { authClient } from '../lib/api';
import { useState, type CSSProperties, type FormEvent, type ReactNode, type SVGProps } from 'react';
import { formatEuro, TitleBlock } from '../components/forms';
import type { WeekHours } from '../components/WeekHoursEditor';
import { bookingUrl, ShareLinkButton } from './booking-settings';
import { weekdayOf } from '@salon/scheduling';
import { dateOf, formatDateShort, SALON_TZ, todayIn } from '../lib/time';
import { CardIcon, ClockIcon, LinkIcon, LockIcon, MailIcon, PinIcon, PlugIcon, ScissorsIcon, StoreIcon, TeamIcon, UserIcon } from '../components/icons';
import { CalendarFeed } from '../components/CalendarFeed';
import { useCurrentLocation, useLocations } from '../lib/location';
import { Button, Card, EmptyState, ErrorNote, Field } from '../components/ui';
import { api, ApiError, type Me, type Salon } from '../lib/api';
import { formatToday, setLocale, useLocale, useT, type TKey } from '../lib/i18n';

const types = ['hair', 'barber', 'nails', 'beauty', 'pedicure', 'massage', 'lashes_brows', 'other'] as const;

/** First step after signup: name the salon and pick the business type. */
export function StartPage() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [type, setType] = useState<(typeof types)[number]>('hair');
  const create = useMutation({
    mutationFn: (body: { name: string; businessType: string }) =>
      api<{ salon: { id: string } }>('/orgs', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: async ({ salon }) => {
      // Drop the cached "me" so the route guard sees the new salon.
      queryClient.removeQueries({ queryKey: ['me'] });
      navigate({ to: '/s/$orgId/welkom', params: { orgId: salon.id } });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    create.mutate({ name: String(new FormData(e.currentTarget).get('salonName')), businessType: type });
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <form onSubmit={onSubmit} className="flex w-full max-w-[520px] flex-col gap-6">
        <div>
          <h1 className="text-[28px] font-bold leading-tight">{t('start.title')}</h1>
          <p className="mt-2 text-muted">{t('start.subtitle')}</p>
        </div>
        <Field id="salonName" name="salonName" required minLength={2} maxLength={80} label={t('start.salonName')} />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-semibold">{t('start.type')}</legend>
          <div className="flex flex-wrap gap-2">
            {types.map((value) => (
              <label
                key={value}
                className={`cursor-pointer rounded-full border px-4 py-2 text-[15px] font-medium has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
                  type === value ? 'border-ink bg-ink text-white' : 'border-line bg-surface'
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value={value}
                  checked={type === value}
                  onChange={() => setType(value)}
                  className="sr-only"
                />
                {t(`type.${value}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <ErrorNote message={create.error instanceof ApiError ? create.error.message : create.error ? t('error.generic') : null} />
        <Button type="submit" disabled={create.isPending} className="self-start">
          {t('start.submit')}
        </Button>
      </form>
    </div>
  );
}

type HubItem = { key: TKey; desc: TKey; Icon: (p: SVGProps<SVGSVGElement>) => ReactNode; to?: string; meta?: ReactNode; extra?: ReactNode; badge?: ReactNode };

/** One area of the salon: an icon, what it is, how it stands, and a glimpse of it. Not built yet: a dashed tile. */
function HubTile({ orgId, item, warn = false }: { orgId: string; item: HubItem; warn?: boolean }) {
  const t = useT();
  const body = (
    <>
      <span className="flex items-start gap-4">
        <span className={`flex size-11 shrink-0 items-center justify-center rounded-full ${item.to ? 'bg-subtle text-ink' : 'bg-transparent text-muted'}`}>
          <item.Icon />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className={`font-semibold ${item.to ? '' : 'text-muted'}`}>{t(item.key)}</span>
            {!item.to && <span className="tag bg-subtle text-muted">{t('today.setup.soon')}</span>}
            {item.badge}
          </span>
          <span className="mt-0.5 block text-sm text-muted">{t(item.desc)}</span>
          {item.meta && <span className={`mt-2 block text-[13px] font-medium ${warn ? 'text-warning' : 'text-ink/70'}`}>{item.meta}</span>}
        </span>
        {item.to && (
          <span aria-hidden className="mt-2.5 text-muted transition-transform group-hover:translate-x-0.5">
            →
          </span>
        )}
      </span>
      {item.extra && <span className="mt-4 block border-t border-line pt-4">{item.extra}</span>}
    </>
  );
  if (!item.to) return <div className="flex h-full flex-col rounded-[var(--radius-card)] border border-dashed border-line p-5">{body}</div>;
  return (
    <Link
      to={item.to}
      params={{ orgId }}
      className="group flex h-full flex-col rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[0_1px_2px_rgb(29_25_21/0.04)] transition-shadow hover:shadow-[0_6px_20px_rgb(29_25_21/0.08)]"
    >
      {body}
    </Link>
  );
}

type Overview = {
  services: { count: number; online: number; minPriceCents: number | null; maxPriceCents: number | null };
  team: Array<{ id: string; displayName: string; color: string; login: boolean }>;
  onlineBookings30d: number;
  messages30d: { sent: number; failed: number };
};
type Holiday = { date: string; name: { nl: string; en: string }; closed: boolean };
type Closure = { dateFrom: string; dateUntil: string; message: string | null };
const euro = (cents: number) => formatEuro(cents).replace(/,00$/, '');

/** The team as overlapping colour circles, the way they look in the agenda. */
function TeamFaces({ team }: { team: Overview['team'] }) {
  return (
    <span className="flex items-center">
      {team.slice(0, 8).map((m, i) => (
        <span
          key={m.id}
          title={m.displayName}
          className={`flex size-8 items-center justify-center rounded-full text-[12px] font-semibold text-white ring-2 ring-surface ${i > 0 ? '-ml-2' : ''}`}
          style={{ backgroundColor: m.color }}
        >
          {m.displayName.slice(0, 1).toUpperCase()}
        </span>
      ))}
      <span className="ml-3 truncate text-[13px] text-muted">{team.map((m) => m.displayName).join(', ')}</span>
    </span>
  );
}

/** Opening hours as a week strip, today marked with the ribbon. */
function WeekStrip({ hours }: { hours: WeekHours }) {
  const t = useT();
  const locale = useLocale();
  const today = String(weekdayOf(todayIn(SALON_TZ)));
  // "09:00" → "9", "09:30" → "9:30": a strip of seven days has little room.
  const short = (h: string) => h.replace(/^0/, '').replace(/:00$/, '');
  return (
    <span className="grid grid-cols-7 gap-1 text-center">
      {(['1', '2', '3', '4', '5', '6', '7'] as const).map((d) => {
        const ranges = hours[d] ?? [];
        const name = new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, Number(d)))).slice(0, 2);
        return (
          <span key={d} className={`flex flex-col gap-1 rounded-[10px] px-0.5 py-1.5 ${d === today ? 'bg-accent-soft' : ''}`}>
            <span className={`eyebrow text-[10px] ${d === today ? 'text-accent-strong' : ''}`}>{name}</span>
            {ranges.length === 0 ? (
              <span className="text-[12px] text-muted/70">{t('hub.closed')}</span>
            ) : (
              ranges.map((r) => (
                <span key={r.start} className="text-[12px] font-medium leading-tight tabular-nums">
                  {short(r.start)}–{short(r.end)}
                </span>
              ))
            )}
          </span>
        );
      })}
    </span>
  );
}

/** The booking link, ready to share: the thing a salon sends most. */
function BookingLinkCard({ orgId, salon }: { orgId: string; salon: Salon['salon'] }) {
  const t = useT();
  const url = bookingUrl(salon.slug);
  return (
    <section className="relative flex h-full flex-col overflow-hidden rounded-[var(--radius-card)] bg-frame p-5 text-canvas">
      <div aria-hidden className="pointer-events-none absolute -right-16 -top-20 size-56 rounded-full bg-accent/25 blur-3xl" />
      <p className="eyebrow relative text-canvas/60">{t('hub.link')}</p>
      <p className="display relative mt-2 text-[32px] leading-tight">{salon.name}</p>
      <p className="relative mt-1 truncate text-[14px] text-canvas/70">{url.replace(/^https?:\/\//, '')}</p>
      <p className="relative mt-3 text-[14px] text-canvas/70">{t('hub.linkBody')}</p>
      <div className="relative mt-auto flex flex-wrap gap-2 pt-4">
        <ShareLinkButton orgId={orgId} slug={salon.slug} variant="primary" />
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center rounded-full border border-canvas/20 px-5 text-[15px] font-medium text-canvas hover:bg-canvas/10">
          {t('hub.open')}
        </a>
      </div>
    </section>
  );
}

/** The Salon hub: everything you set up once, in three groups (plan 2.3), each tile with how it stands. */
export function SalonHubPage() {
  const t = useT();
  const { orgId } = useParams({ from: '/s/$orgId/salon' });
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  const locations = useLocations(orgId);
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isManager = salon.data?.role === 'owner' || salon.data?.role === 'manager';
  const location = useCurrentLocation(orgId);
  const base = `/orgs/${orgId}/locations/${location.data?.id}`;
  const enabled = isManager && !!location.data;
  const overview = useQuery({ queryKey: ['salon-overview', orgId], queryFn: () => api<Overview>(`/orgs/${orgId}/salon-overview`), enabled: isManager });
  const hours = useQuery({ queryKey: ['hours', location.data?.id], queryFn: () => api<{ hours: WeekHours }>(`${base}/hours`), enabled });
  const holidays = useQuery({ queryKey: ['holidays', location.data?.id], queryFn: () => api<{ holidays: Holiday[] }>(`${base}/holidays`), enabled });
  const closures = useQuery({ queryKey: ['closures', location.data?.id], queryFn: () => api<{ closures: Closure[] }>(`${base}/closures`), enabled });
  const booking = useQuery({ queryKey: ['booking-settings', orgId], queryFn: () => api<{ bookingEnabled: boolean }>(`/orgs/${orgId}/booking-settings`), enabled: isManager });
  const messages = useQuery({ queryKey: ['message-settings', orgId], queryFn: () => api<{ reminder: { enabled: boolean; hoursBefore: number } }>(`/orgs/${orgId}/message-settings`), enabled: isManager });
  const privacy = useQuery({ queryKey: ['privacy', orgId], queryFn: () => api<{ dpa: { acceptedAt: string | null; upToDate: boolean }; supportAccessUntil: string | null }>(`/orgs/${orgId}/privacy`), enabled: isManager });

  async function logout() {
    await authClient.signOut();
    queryClient.clear();
    navigate({ to: '/inloggen' });
  }
  type To = '/s/$orgId/salon/diensten' | '/s/$orgId/salon/team' | '/s/$orgId/salon/openingstijden' | '/s/$orgId/salon/bedrijf' | '/s/$orgId/salon/online-boeken' | '/s/$orgId/salon/berichten' | '/s/$orgId/salon/locaties' | '/s/$orgId/salon/privacy' | '/s/$orgId/salon/abonnement';
  type Item = HubItem & { to?: To; warn?: boolean };
  const o = overview.data;
  const day = (date: string) => formatDateShort(date, locale);
  const today = todayIn(SALON_TZ);

  // The next day the salon is closed: a public holiday it closes on, or its own closure.
  const nextClosed = [
    ...(holidays.data?.holidays ?? []).filter((h) => h.closed && h.date >= today).map((h) => ({ date: h.date, name: h.name[locale] })),
    ...(closures.data?.closures ?? []).filter((c) => c.dateUntil >= today).map((c) => ({ date: c.dateFrom < today ? today : c.dateFrom, name: c.message ?? t('cal.closed') })),
  ].sort((a, b) => a.date.localeCompare(b.date))[0];

  const logins = o?.team.filter((m) => m.login).length ?? 0;
  const services: Item = {
    key: 'salon.services',
    desc: 'hub.services',
    Icon: ScissorsIcon,
    to: '/s/$orgId/salon/diensten',
    meta: o ? [t('hub.servicesCount', { n: o.services.count }), o.services.count > 0 && t('hub.onlineCount', { n: o.services.online })].filter(Boolean).join(' · ') : undefined,
    extra:
      o && o.services.minPriceCents !== null && o.services.maxPriceCents !== null ? (
        <span className="display text-[28px] leading-none">
          {o.services.minPriceCents === o.services.maxPriceCents ? euro(o.services.maxPriceCents) : `${euro(o.services.minPriceCents)} – ${euro(o.services.maxPriceCents)}`}
        </span>
      ) : undefined,
  };
  const team: Item = {
    key: 'salon.team',
    desc: 'hub.team',
    Icon: TeamIcon,
    to: '/s/$orgId/salon/team',
    meta: o ? `${t('hub.teamCount', { n: o.team.length })} · ${logins === 1 ? t('hub.loginOne') : t('hub.loginCount', { n: logins })}` : undefined,
    extra: o && o.team.length > 0 ? <TeamFaces team={o.team} /> : undefined,
  };
  const hoursTile: Item = {
    key: 'salon.hours',
    desc: 'hub.hours',
    Icon: ClockIcon,
    to: '/s/$orgId/salon/openingstijden',
    meta: nextClosed ? t('hub.nextClosed', { date: day(nextClosed.date), name: nextClosed.name }) : undefined,
    extra: hours.data ? <WeekStrip hours={hours.data.hours} /> : undefined,
  };
  const bookingTile: Item = {
    key: 'salon.booking',
    desc: 'hub.booking',
    Icon: LinkIcon,
    to: '/s/$orgId/salon/online-boeken',
    meta: o ? t('hub.bookings30', { n: o.onlineBookings30d }) : undefined,
    badge: booking.data ? (
      <span className={`tag ${booking.data.bookingEnabled ? 'bg-success-soft text-success' : 'bg-subtle text-muted'}`}>{booking.data.bookingEnabled ? t('hub.bookingOn') : t('hub.bookingOff')}</span>
    ) : undefined,
  };
  const messagesTile: Item = {
    key: 'salon.messages',
    desc: 'hub.messages',
    Icon: MailIcon,
    to: '/s/$orgId/salon/berichten',
    meta: messages.data ? (messages.data.reminder.enabled ? t('hub.reminder', { h: messages.data.reminder.hoursBefore }) : t('hub.reminderOff')) : undefined,
    extra: o ? (
      <span className="flex flex-wrap gap-x-3 text-[13px] text-muted">
        <span>{t('hub.sent30', { n: o.messages30d.sent })}</span>
        {o.messages30d.failed > 0 && <span className="font-medium text-danger">{t('hub.failed30', { n: o.messages30d.failed })}</span>}
      </span>
    ) : undefined,
  };
  const p = privacy.data;
  const groups: Array<{ title: TKey; items: Item[]; after?: ReactNode }> = isManager
    ? [
        {
          title: 'salon.group.salon',
          items: [
            services,
            team,
            hoursTile,
            ...((locations.data?.length ?? 0) > 1 ? [{ key: 'loc.title' as TKey, desc: 'hub.locations' as TKey, Icon: PinIcon, to: '/s/$orgId/salon/locaties' as const, meta: t('hub.locationsCount', { n: locations.data!.length }) }] : []),
          ],
        },
        {
          title: 'salon.group.reach',
          items: [bookingTile, messagesTile],
          after: salon.data && <BookingLinkCard orgId={orgId} salon={salon.data.salon} />,
        },
        {
          title: 'salon.group.account',
          items: [
            { key: 'salon.business', desc: 'hub.business', Icon: StoreIcon, to: '/s/$orgId/salon/bedrijf', meta: salon.data?.salon.name },
            {
              key: 'salon.privacy',
              desc: 'hub.privacy',
              Icon: LockIcon,
              to: '/s/$orgId/salon/privacy',
              meta: p ? (p.supportAccessUntil ? t('hub.supportOn', { date: day(dateOf(p.supportAccessUntil, SALON_TZ)) }) : p.dpa.acceptedAt && p.dpa.upToDate ? t('hub.dpaSigned', { date: day(dateOf(p.dpa.acceptedAt, SALON_TZ)) }) : t('hub.dpaMissing')) : undefined,
              warn: !!p && (!p.dpa.upToDate || !!p.supportAccessUntil),
            },
            ...(salon.data?.role === 'owner' ? [{ key: 'salon.subscription' as TKey, desc: 'hub.subscription' as TKey, Icon: CardIcon, to: '/s/$orgId/salon/abonnement' as const }] : []),
            { key: 'salon.integrations', desc: 'hub.integrations', Icon: PlugIcon },
          ],
        },
      ]
    : [];

  // What goes in each column: the tiles, with the "binnenkort" tiles stacked in one cell, then any extra card.
  function groupCells(g: (typeof groups)[number]) {
    const ready = g.items.filter((i) => i.to);
    const soon = g.items.filter((i) => !i.to);
    const cells: Array<{ node: ReactNode; wide?: boolean }> = ready.map((item) => ({ node: <HubTile orgId={orgId} item={item} warn={item.warn} /> }));
    if (soon.length > 0)
      cells.push({
        node: (
          <div className="flex h-full flex-col gap-3 sm:flex-row xl:flex-col [&>*]:flex-1">
            {soon.map((item) => (
              <HubTile key={item.key} orgId={orgId} item={item} />
            ))}
          </div>
        ),
      });
    if (g.after) cells.push({ node: g.after, wide: true });
    // Two columns (tablet): an odd last cell takes the whole row instead of sitting alone.
    if (cells.length % 2 === 1) cells[cells.length - 1]!.wide = true;
    return cells;
  }
  const rows = Math.max(0, ...groups.map((g) => groupCells(g).length));

  return (
    <div className="flex w-full flex-col gap-10">
      <TitleBlock title={t('salon.title')} intro={isManager ? t('hub.intro') : t('salon.staffNote')} />
      {/* Wide screens: the three groups side by side, like the pages of a menu. */}
      {groups.length > 0 && (
        // Wide screens: one grid where the groups are columns and the tiles share rows,
        // so every row has one height and the three columns end on the same line.
        <div className="flex flex-col gap-10 xl:grid xl:grid-cols-3 xl:gap-x-5 xl:gap-y-3">
          {groups.map((g, gi) => {
            const cells = groupCells(g);
            return (
              <section key={g.title} className="flex flex-col gap-3 xl:contents">
                <h2 className="eyebrow xl:[grid-column:var(--col)] xl:[grid-row:1]" style={{ '--col': gi + 1 } as CSSProperties}>
                  {t(g.title)}
                </h2>
                <div className="grid gap-3 sm:grid-cols-2 xl:contents">
                  {cells.map((cell, ci) => (
                    <div
                      key={ci}
                      className={`${cell.wide ? 'sm:col-span-2' : ''} xl:[grid-column:var(--col)] xl:[grid-row:var(--row)/span_var(--span)]`}
                      style={{ '--col': gi + 1, '--row': ci + 2, '--span': ci === cells.length - 1 ? rows - cells.length + 1 : 1 } as CSSProperties}
                    >
                      {cell.node}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {/* Just for you: your own login and your own calendar. */}
      <section className="flex flex-col gap-3">
        <h2 className="eyebrow">{t('hub.you')}</h2>
        <div className="grid gap-3 lg:grid-cols-2">
          <HubTile
            orgId={orgId}
            item={{
              key: 'acc.title',
              desc: 'hub.account',
              Icon: UserIcon,
              to: '/s/$orgId/account' as To,
              meta: me.data ? (me.data.user.twoFactorEnabled ? t('acc.hubHint2faOn') : t('acc.hubHint2fa')) : undefined,
            }}
            warn={!!me.data && !me.data.user.twoFactorEnabled}
          />
          {salon.data?.staffMemberId && (
            <Card className="[&>section]:border-0 [&>section]:pt-0">
              <CalendarFeed orgId={orgId} staffMemberId={salon.data.staffMemberId} own />
            </Card>
          )}
        </div>
      </section>
      {/* Account actions, also reachable on phones (the sidebar is hidden there). */}
      <section className="flex flex-col gap-3 md:hidden">
        <h2 className="eyebrow">{me.data?.user.name}</h2>
        <div className="flex flex-col gap-2">
          <Button variant="secondary" onClick={() => setLocale(locale === 'nl' ? 'en' : 'nl')}>
            {t('account.language')}
          </Button>
          <Button variant="secondary" onClick={logout}>
            {t('account.logout')}
          </Button>
        </div>
      </section>
    </div>
  );
}
